import { useState, useEffect, useRef, useCallback } from "react";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { PartialBlock } from "@blocknote/core";
import { Copy, Download, Trash2, Save, AlertCircle, Cloud, CloudOff } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useUpdateStudyNote, useDeleteStudyNote, useCreateStudyNote, StudyNote } from "@/hooks/useQueryHooks";
import { supabase } from "@/integrations/supabase/client";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
export interface StudyNoteEditorRef {
  getSaveStatusIcon: () => JSX.Element;
  getSaveStatusText: () => string;
  handleManualSave: () => void;
  copyToClipboard: () => void;
  downloadNote: () => void;
  isSaving: boolean;
}
interface StudyNoteEditorProps {
  noteId?: string; // Database ID for existing notes
  topicId: string;
  initialTitle?: string;
  initialContent?: any; // BlockNote JSON from database
  existingNote?: StudyNote; // Full note data from database
  onTitleChange?: (title: string) => void;
  onNoteSaved?: (noteId: string, title: string) => void;
  onNoteDeleted?: () => void;
  editorRef?: React.MutableRefObject<StudyNoteEditorRef | null>;
}
export const StudyNoteEditor = ({
  noteId,
  topicId,
  initialTitle = "New note...",
  initialContent,
  existingNote,
  onTitleChange,
  onNoteSaved,
  onNoteDeleted,
  editorRef
}: StudyNoteEditorProps) => {
  const [title, setTitle] = useState(() => {
    // Convert backend "Study Notes for..." format to "New note..."
    const titleToUse = existingNote?.title || initialTitle;
    if (titleToUse?.startsWith('Study Notes for ')) {
      return 'New note...';
    }
    return titleToUse;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(existingNote ? new Date(existingNote.updated_at) : null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>();
  const isNewNote = !noteId;
  const {
    toast
  } = useToast();
  const updateNoteMutation = useUpdateStudyNote();
  const createNoteMutation = useCreateStudyNote();
  const deleteNoteMutation = useDeleteStudyNote();

  // Migrate from localStorage on first load if it's a new note
  const getInitialContent = () => {
    if (existingNote?.content) {
      return existingNote.content;
    }
    if (initialContent) {
      return initialContent;
    }

    // Try to migrate from localStorage for new notes
    if (isNewNote) {
      const localStorageKey = `study-note-new-${topicId}`;
      const savedContent = localStorage.getItem(localStorageKey);
      if (savedContent) {
        try {
          return JSON.parse(savedContent);
        } catch (error) {
          console.error('Error parsing localStorage content:', error);
        }
      }
    }
    return [{
      type: "paragraph",
      content: "Start writing your notes here..."
    }];
  };

  // Create the editor with initial content
  const editor = useCreateBlockNote({
    initialContent: getInitialContent() as PartialBlock[]
  });

  // Auto-save function with Supabase
  const saveToDatabase = useCallback(async (titleToSave: string, contentToSave: any) => {
    if (!topicId) return;
    
    // Prevent multiple simultaneous saves
    if (isSaving) {
      console.log('🚫 Save already in progress, skipping');
      return;
    }
    
    setIsSaving(true);
    setSaveError(null);
    try {
      if (isNewNote || !noteId) {
        // Create new note with better error handling
        console.log('📝 Creating new note with:', {
          topicId,
          title: titleToSave,
          contentLength: Array.isArray(contentToSave) ? contentToSave.length : 0
        });
        
        const newNote = await createNoteMutation.mutateAsync({
          topic_id: topicId,
          title: titleToSave,
          content: contentToSave
        });
        
        console.log('✅ New note created successfully:', newNote.id);
        setLastSaved(new Date());
        onNoteSaved?.(newNote.id, titleToSave);

        // Clear localStorage backup for new notes
        localStorage.removeItem(`study-note-new-${topicId}`);
        toast({
          title: "Note saved",
          description: "Your note has been saved to the cloud"
        });
      } else {
        // Update existing note - make sure noteId is a valid UUID
        console.log('Updating existing note with ID:', noteId, 'and data:', {
          title: titleToSave
        });
        await updateNoteMutation.mutateAsync({
          id: noteId,
          // This should be the actual database UUID
          topic_id: topicId,
          title: titleToSave,
          content: contentToSave
        });
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('Error saving note:', error);
      setSaveError('Failed to save note');

      // Fallback to localStorage on error
      const backupKey = noteId || `study-note-new-${topicId}`;
      localStorage.setItem(`study-note-backup-${backupKey}`, JSON.stringify({
        title: titleToSave,
        content: contentToSave,
        timestamp: new Date().toISOString()
      }));
      toast({
        title: "Save failed",
        description: "Note saved locally as backup",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  }, [topicId, noteId, isNewNote, createNoteMutation, updateNoteMutation, onNoteSaved, toast]);

  // Debounced auto-save with simplified logic
  const debouncedSave = useCallback(() => {
    // Don't save if already saving
    if (isSaving) {
      console.log('🚫 Skipping save - already saving');
      return;
    }
    
    // Don't auto-save if we don't have a noteId (shouldn't happen anymore)
    if (!noteId) {
      console.warn('⚠️ Skipping auto-save - no noteId available');
      return;
    }
    
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      const content = editor.document;
      console.log('💾 Auto-saving existing note...');
      saveToDatabase(title, content);
    }, 2000); // 2 second debounce
  }, [title, editor, saveToDatabase, isSaving, noteId]);

  // Handle content changes
  const handleChange = useCallback(() => {
    debouncedSave();
  }, [debouncedSave]);

  // Handle title changes
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    onTitleChange?.(newTitle);
    debouncedSave();
  };

  // Manual save
  const handleManualSave = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    saveToDatabase(title, editor.document);
  };

  // Delete note - simplified since we always have noteId
  const handleDeleteNote = async () => {
    if (!noteId) {
      toast({
        title: "Cannot delete",
        description: "Invalid note ID",
        variant: "destructive"
      });
      return;
    }
    
    const confirmed = window.confirm("Are you sure you want to delete this note? This action cannot be undone.");
    if (confirmed) {
      try {
        await deleteNoteMutation.mutateAsync({
          id: noteId,
          topic_id: topicId
        });
        onNoteDeleted?.();
        toast({
          title: "Note deleted",
          description: "Note has been permanently deleted"
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete note",
          variant: "destructive"
        });
      }
    }
  };

  // Copy note content to clipboard
  const copyToClipboard = async () => {
    try {
      const blocks = editor.document;
      const textContent = blocks.map(block => {
        const blockContent = (block as any).content;
        if (typeof blockContent === 'string') {
          return blockContent;
        } else if (Array.isArray(blockContent)) {
          return blockContent.map(content => typeof content === 'string' ? content : (content as any).text || '').join('');
        }
        return '';
      }).filter(text => text.trim()).join('\n\n');
      await navigator.clipboard.writeText(`${title}\n\n${textContent}`);
      toast({
        title: "Copied!",
        description: "Note content copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy note content",
        variant: "destructive"
      });
    }
  };

  // Download note as markdown
  const downloadNote = () => {
    try {
      const blocks = editor.document;
      const markdownContent = blocks.map(block => {
        const blockContent = (block as any).content;
        const blockText = typeof blockContent === 'string' ? blockContent : Array.isArray(blockContent) ? blockContent.map(content => typeof content === 'string' ? content : (content as any).text || '').join('') : '';
        switch ((block as any).type) {
          case 'heading':
            const level = (block as any).props?.level || 1;
            return '#'.repeat(level) + ' ' + blockText;
          case 'bulletListItem':
            return '- ' + blockText;
          case 'numberedListItem':
            return '1. ' + blockText;
          default:
            return blockText;
        }
      }).filter(text => text.trim()).join('\n\n');
      const content = `# ${title}\n\n${markdownContent}`;
      const blob = new Blob([content], {
        type: 'text/markdown'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
      link.click();
      URL.revokeObjectURL(url);
      toast({
        title: "Downloaded!",
        description: "Note downloaded as markdown file"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download note",
        variant: "destructive"
      });
    }
  };

  // Clear note content
  const clearNote = () => {
    const confirmed = window.confirm("Are you sure you want to clear this note? This action cannot be undone.");
    if (confirmed) {
      editor.replaceBlocks(editor.document, [{
        type: "paragraph",
        content: "Start writing your notes here..."
      }]);
      debouncedSave();
      toast({
        title: "Note cleared",
        description: "Note content has been cleared"
      });
    }
  };

  // Set up realtime subscription for note updates
  useEffect(() => {
    if (!noteId) return;
    const channel = supabase.channel(`study-note-${noteId}`).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'study_notes',
      filter: `id=eq.${noteId}`
    }, payload => {
      console.log('Note updated in real-time:', payload);
      // Could implement conflict resolution here if needed
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [noteId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Expose editor methods to parent component
  useEffect(() => {
    if (editorRef) {
      editorRef.current = {
        getSaveStatusIcon,
        getSaveStatusText,
        handleManualSave,
        copyToClipboard,
        downloadNote,
        isSaving
      };
    }
  }, [editorRef, isSaving, lastSaved, saveError]);
  const getSaveStatusIcon = () => {
    if (isSaving) return <Save className="w-4 h-4 animate-spin" />;
    if (saveError) return <AlertCircle className="w-4 h-4 text-red-500" />;
    if (lastSaved) return <Cloud className="w-4 h-4 text-green-500" />;
    return <CloudOff className="w-4 h-4 text-gray-400" />;
  };
  const getSaveStatusText = () => {
    if (isSaving) return "Saving...";
    if (saveError) return "Save failed";
    if (lastSaved) return `Saved ${formatDistanceToNow(lastSaved, {
      addSuffix: true
    })}`;
    return "Not saved";
  };
  return <div className="h-full flex flex-col">

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full pt-6 px-0">
          <BlockNoteView 
            editor={editor} 
            theme="light" 
            className="h-full [&_.bn-editor]:!bg-transparent [&_.ProseMirror]:!bg-transparent" 
            onChange={handleChange} 
          />
        </div>
      </div>
    </div>;
};